
import { useState } from "react";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle, GlassCardDescription } from "@/components/ui/glass-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { DeviceStatusDistributionChart } from "./DeviceStatusDistributionChart";
import { OSDistributionChart } from "./OSDistributionChart";
import { DeviceActivityChart } from "./DeviceActivityChart";
import { PatchingComplianceChart } from "./PatchingComplianceChart";
import AppVersionDistributionChart from "./AppVersionDistributionChart";
import AppCountChart from "./AppCountChart";
import PlatformAppDistributionChart from "./PlatformAppDistributionChart";
import AppCategoriesChart from "./AppCategoriesChart";
import AppActivityChart from "./AppActivityChart";
import { useDevices } from "@/hooks/useDevices";
import { useApplications } from "@/hooks/useApplications";
import { getDeviceStatus } from "@/utils/deviceStatus";
import { calculateAppDeploymentStats } from "@/utils/appAnalytics";

export function DashboardAnalytics() {
  const [timeframe, setTimeframe] = useState("weekly");
  const { data: devices = [] } = useDevices();
  const { applications } = useApplications();
  
  // Calculate device counts
  const counts = devices.reduce((acc, device) => {
    const status = getDeviceStatus(device.last_seen || device.created_at, device.status);
    acc[status]++;
    acc.total++;
    return acc;
  }, { active: 0, inactive: 0, pending: 0, total: 0 });

  // Calculate app deployment statistics
  const appStats = calculateAppDeploymentStats(devices, applications);
  
  // Define the new aesthetic colors from the image
  const colors = {
    purple: {
      light: '#A5B4FC',  // Light Purple
      medium: '#6366F1',  // Medium Purple
      dark: '#4F46E5',    // Dark Purple
    },
    green: {
      light: '#86EFAC',   // Light Green
      medium: '#4ADE80',  // Medium Green
      dark: '#22C55E',    // Dark Green
    },
    orange: {
      light: '#FFBB80',   // Light Orange
      medium: '#FF8C66',  // Medium Orange
      dark: '#FF5A1F',    // Dark Orange
    }
  };

  const deviceData = [{
    name: 'macOS',
    total: 124,
    active: 110,
    inactive: 8,
    pending: 6
  }, {
    name: 'Windows',
    total: 98,
    active: 85,
    inactive: 7,
    pending: 6
  }, {
    name: 'Linux',
    total: 45,
    active: 39,
    inactive: 4,
    pending: 2
  }, {
    name: 'iOS',
    total: 78,
    active: 72,
    inactive: 3,
    pending: 3
  }, {
    name: 'Android',
    total: 52,
    active: 46,
    inactive: 4,
    pending: 2
  }];

  const securityComplianceRate = 88;
  const patchingComplianceRate = 92;
  const managedDevicesRate = 96;
  
  return <div className="space-y-6 font-poppins">
      {/* Device Activity Chart */}
      <GlassCard variant="default">
        <GlassCardHeader className="flex flex-row items-center justify-between">
          <GlassCardTitle className="text-lg font-medium">Device Status Overview</GlassCardTitle>
          <Tabs defaultValue="weekly" className="w-[300px]">
            <TabsList className="grid w-full grid-cols-3 text-lg font-medium">
              <TabsTrigger value="daily" onClick={() => setTimeframe("daily")}>
                Daily
              </TabsTrigger>
              <TabsTrigger value="weekly" onClick={() => setTimeframe("weekly")}>
                Weekly
              </TabsTrigger>
              <TabsTrigger value="monthly" onClick={() => setTimeframe("monthly")}>
                Monthly
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </GlassCardHeader>
        <GlassCardContent>
          <DeviceActivityChart timeframe={timeframe} />
        </GlassCardContent>
      </GlassCard>

      {/* Application Analytics Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold tracking-tight">Application Analytics</h2>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Total Apps: {appStats.totalManagedApps}</span>
            <span>Avg per Device: {appStats.avgAppsPerDevice}</span>
            <span>Compliance: {appStats.complianceRate}%</span>
          </div>
        </div>
        
        {/* App Version Distribution - Full Width */}
        <AppVersionDistributionChart devices={devices} applications={applications} />
        
        {/* App Analytics Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          <AppCountChart devices={devices} applications={applications} />
          <PlatformAppDistributionChart applications={applications} />
        </div>
        
        <div className="grid gap-6 md:grid-cols-2">
          <AppCategoriesChart applications={applications} />
          <AppActivityChart />
        </div>
      </div>

      {/* Device Analytics Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">

        <GlassCard variant="default">
          <GlassCardHeader>
            <GlassCardTitle className="text-lg font-medium">Device Status Distribution</GlassCardTitle>
            <GlassCardDescription>Current status distribution of all enrolled devices</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="flex justify-center items-center h-[300px] px-2">
            <DeviceStatusDistributionChart counts={counts} />
          </GlassCardContent>
        </GlassCard>

        <GlassCard variant="default">
          <GlassCardHeader>
            <GlassCardTitle className="text-lg font-medium">OS Distribution</GlassCardTitle>
            <GlassCardDescription>Operating system distribution across device fleet</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="flex justify-center items-center h-[300px] px-2">
            <OSDistributionChart />
          </GlassCardContent>
        </GlassCard>

        <GlassCard variant="default">
          <GlassCardHeader>
            <GlassCardTitle className="text-lg font-medium">Patching Compliance</GlassCardTitle>
            <GlassCardDescription>Overview of update status across different security categories</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="h-[300px] flex flex-col justify-between">
            <PatchingComplianceChart />
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* Device Platform Breakdown and Security Compliance */}
      <div className="grid gap-6 md:grid-cols-2">
        <GlassCard variant="default">
          <GlassCardHeader>
            <GlassCardTitle className="text-lg font-medium">Device Platform Breakdown</GlassCardTitle>
            <GlassCardDescription>Distribution of device status by operating system platform</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deviceData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 20
              }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                <XAxis dataKey="name" tick={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 11
                }} />
                <YAxis tick={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 11
                }} />
                <Tooltip formatter={(value, name) => [`${value} devices`, name]} labelFormatter={label => `Platform: ${label}`} contentStyle={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px'
                }} />
                <Legend wrapperStyle={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 11
                }} />
                <Bar dataKey="active" stackId="a" name="Active" fill={colors.purple.medium} /> 
                <Bar dataKey="inactive" stackId="a" name="Inactive" fill={colors.orange.medium} /> 
                <Bar dataKey="pending" stackId="a" name="Pending" fill={colors.green.medium} /> 
              </BarChart>
            </ResponsiveContainer>
          </GlassCardContent>
        </GlassCard>

        <GlassCard variant="default">
          <GlassCardHeader>
            <GlassCardTitle className="text-lg font-medium">Security Compliance</GlassCardTitle>
            <GlassCardDescription>Progress toward key security compliance objectives</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent className="space-y-8">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Overall Security Compliance</span>
                <span className="text-sm font-medium">{securityComplianceRate}%</span>
              </div>
              <Progress value={securityComplianceRate} className="h-2 bg-gray-200">
                <div className="h-full rounded-full" style={{ backgroundColor: colors.purple.medium }} />
              </Progress>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Patching Compliance</span>
                <span className="text-sm font-medium">{patchingComplianceRate}%</span>
              </div>
              <Progress value={patchingComplianceRate} className="h-2 bg-gray-200">
                <div className="h-full rounded-full" style={{ backgroundColor: colors.green.medium }} />
              </Progress>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">App Deployment Health</span>
                <span className="text-sm font-medium">{appStats.complianceRate}%</span>
              </div>
              <Progress value={appStats.complianceRate} className="h-2 bg-gray-200">
                <div className="h-full rounded-full" style={{ backgroundColor: colors.orange.medium }} />
              </Progress>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    </div>;
}
